import { useState, useEffect, useRef } from 'react';
import { ExternalLink, X, ChevronLeft, ChevronRight } from 'lucide-react';
import ImageLoader from '@/components/ImageLoader';
import portfolio1SourceLogo from '@/assets/portfolio-1source-logo.jpg';
import portfolio1SourceScreens from '@/assets/portfolio-1source-screens.jpg';
import portfolioMoveMarketLogo from '@/assets/portfolio-move-network-logo.jpg';
import portfolioMoveMarketCollection from '@/assets/portfolio-move-market-collection.jpg';
import portfolioMoveMarketWallet from '@/assets/portfolio-move-market-wallet.jpg';
import portfolioMoveMarketDetailHistory from '@/assets/portfolio-move-market-detail-history.jpg';
import portfolioMoveMarketDetailBid from '@/assets/portfolio-move-market-detail-bid.jpg';
import portfolioFreshupLogo from '@/assets/portfolio-freshup-logo.jpg';
import portfolioFreshup1 from '@/assets/portfolio-freshup-1-welcome.png';
import portfolioFreshup2 from '@/assets/portfolio-freshup-2-scan.png';
import portfolioFreshup3 from '@/assets/portfolio-freshup-3-nutrition.png';
import portfolioFreshup4 from '@/assets/portfolio-freshup-4-checkout.png';
import portfolioFreshup5 from '@/assets/portfolio-freshup-5-payment.png';
import portfolioFreshup6 from '@/assets/portfolio-freshup-6-complete.png';
import portfolioOwlsocialLogo from '@/assets/portfolio-owlsocial-logo.png';
import portfolioOwlsocial1 from '@/assets/portfolio-owlsocial-1-browse.jpg';
import portfolioOwlsocial2 from '@/assets/portfolio-owlsocial-2-profile.jpg';
import portfolioOwlsocial3 from '@/assets/portfolio-owlsocial-3-connected.png';
import portfolioOwlsocial4 from '@/assets/portfolio-owlsocial-4-chat.jpg';
import portfolioVisibleIngredientsLogo from '@/assets/portfolio-visible-ingredients-logo.jpg';
import portfolioVisibleIngredientsWebsite from '@/assets/portfolio-visible-ingredients-website.jpg';
import portfolioRoseCrownWebsite from '@/assets/portfolio-rosecrown-website.png';
interface PortfolioItem {
  title: string;
  category: string;
  image: string;
  description: string;
  detailImages?: string[];
  imageScale?: number;
  imageBgColor?: string;
}
const portfolioItems: PortfolioItem[] = [{
  title: 'The Owl Social',
  category: 'App Development',
  image: portfolioOwlsocialLogo,
  description: 'Social networking app for connecting people at bars and venues',
  detailImages: [portfolioOwlsocial1, portfolioOwlsocial2, portfolioOwlsocial3, portfolioOwlsocial4],
  imageScale: 0.5,
  imageBgColor: '#441da4'
}, {
  title: '1Source',
  category: 'App Development',
  image: portfolio1SourceLogo,
  description: 'Product ingredient scanner app with health ratings and safety information',
  detailImages: [portfolio1SourceScreens]
}, {
  title: 'FreshUp',
  category: 'Point of Sale System',
  image: portfolioFreshupLogo,
  description: 'Smart self-checkout kiosk with product scanning and nutrition information',
  detailImages: [portfolioFreshup1, portfolioFreshup2, portfolioFreshup3, portfolioFreshup4, portfolioFreshup5, portfolioFreshup6]
}, {
  title: 'Move Market',
  category: 'UI/UX Design',
  image: portfolioMoveMarketLogo,
  description: 'NFT marketplace platform for creating, selling and collecting digital items',
  detailImages: [portfolioMoveMarketCollection, portfolioMoveMarketDetailBid, portfolioMoveMarketDetailHistory, portfolioMoveMarketWallet]
}, {
  title: 'Visible Ingredients',
  category: 'Web Development',
  image: portfolioVisibleIngredientsLogo,
  description: 'Ingredient transparency platform helping consumers make informed product choices',
  detailImages: [portfolioVisibleIngredientsWebsite]
}, {
  title: 'RoseCrown',
  category: 'Web Development',
  image: portfolioRoseCrownWebsite,
  description: 'Custom e-commerce website for a premium beauty and skincare brand',
  detailImages: [portfolioRoseCrownWebsite]
}];
const Portfolio = () => {
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedItem) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedItem]);
  const handleOpenDetail = (item: PortfolioItem) => {
    setSelectedItem(item);
    setCurrentImageIndex(0);
  };
  const handleClose = () => {
    setSelectedItem(null);
    setCurrentImageIndex(0);
  };
  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedItem?.detailImages) {
      setCurrentImageIndex(prev => prev === 0 ? selectedItem.detailImages!.length - 1 : prev - 1);
      scrollContainerRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedItem?.detailImages) {
      setCurrentImageIndex(prev => prev === selectedItem.detailImages!.length - 1 ? 0 : prev + 1);
      scrollContainerRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  return <section id="portfolio" className="section-padding bg-section-alt">
      <div className="container-padding max-w-7xl mx-auto">
        <div className="text-center mb-16 md:mb-20">
          <p className="text-eyebrow font-medium text-sm mb-4">Our Portfolio</p>
          <h2 className="heading-lg text-foreground mb-6">
            Featured work
          </h2>
          <p className="text-body max-w-2xl mx-auto">
            A selection of projects that showcase our expertise and creativity across various industries.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {portfolioItems.map(item => <div key={item.title} className="group relative overflow-hidden rounded-2xl bg-card border border-border hover:border-primary/30 transition-all duration-300 hover-lift cursor-pointer" onClick={() => item.detailImages && handleOpenDetail(item)}>
              <div style={item.imageBgColor ? {
            backgroundColor: item.imageBgColor
          } : undefined} className="aspect-[4/3] overflow-hidden">
                <ImageLoader 
                  src={item.image} 
                  alt={`${item.title} - ${item.category} project by RLX`} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  style={item.imageScale ? {
                    objectFit: 'contain',
                    transform: `scale(${item.imageScale})`
                  } : undefined}
                />
              </div>
              <div className="p-6">
                <p className="text-eyebrow text-sm font-medium mb-2">{item.category}</p>
                <h3 className="text-xl font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
              {item.detailImages && <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-background/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ExternalLink className="w-4 h-4 text-foreground" />
                </div>}
            </div>)}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedItem && selectedItem.detailImages && <>
        {/* Fixed backdrop */}
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm" onClick={handleClose} />
        
        {/* Fixed controls */}
        <button className="fixed top-6 right-6 w-12 h-12 rounded-full bg-foreground flex items-center justify-center hover:bg-foreground/80 transition-colors z-[60] shadow-lg" onClick={handleClose}>
          <X className="w-6 h-6 text-background" />
        </button>
        
        {selectedItem.detailImages.length > 1 && <>
          <button onClick={handlePrevImage} className="fixed left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-foreground flex items-center justify-center hover:bg-foreground/80 transition-colors z-[60] shadow-lg">
            <ChevronLeft className="w-6 h-6 text-background" />
          </button>
          <button onClick={handleNextImage} className="fixed right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-foreground flex items-center justify-center hover:bg-foreground/80 transition-colors z-[60] shadow-lg">
            <ChevronRight className="w-6 h-6 text-background" />
          </button>
        </>}
        
        {/* Scrollable content */}
        <div ref={scrollContainerRef} className="fixed inset-0 z-[55] overflow-y-auto" onClick={handleClose}>
          <div className="min-h-full flex flex-col items-center py-8 px-4 md:px-16" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-8 w-full">
              <p className="text-eyebrow text-sm font-medium mb-2">{selectedItem.category}</p>
              <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">{selectedItem.title}</h3>
              <p className="text-muted-foreground">{selectedItem.description}</p>
            </div>
            
            <div className="max-w-5xl w-full">
              <ImageLoader 
                src={selectedItem.detailImages[currentImageIndex]} 
                alt={`${selectedItem.title} - view ${currentImageIndex + 1}`} 
                className="w-full h-auto rounded-2xl"
                loaderMaxHeight="60vh"
                lazy={false}
              />
              
              {/* Image indicators */}
              {selectedItem.detailImages.length > 1 && <div className="flex justify-center gap-2 mt-6 pb-4">
                {selectedItem.detailImages.map((_, index) => <button key={index} onClick={e => {
                e.stopPropagation();
                setCurrentImageIndex(index);
              }} className={`w-2.5 h-2.5 rounded-full transition-colors ${index === currentImageIndex ? 'bg-primary' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`} />)}
              </div>}
            </div>
          </div>
        </div>
      </>}
    </section>;
};
export default Portfolio;